import { AdjustedProductType } from "../interfaces/DTO/mislaka_xml_models";
import { MimshakData } from "../interfaces/DTO/mimshakData";
import { SimulationMimshakData } from "../interfaces/DTO/simulation";

export interface ExcludedItemsHeuristicResult {
  valid: boolean;
  expectedMinCount: number;
  actualCount: number;
  missingTypes: AdjustedProductType[];
}

/**
 * Validates that excludedSimulationItems contains at least as many records
 * per adjusted_product_type as allData does for excluded types.
 *
 * The excluded set can only grow over time (new policies added), never shrink.
 * So if allData has N records of an excluded type, the simulation must have >= N.
 * A mismatch means excluded items were lost during load or save.
 */
export function validateExcludedItems(
  allData: MimshakData[],
  includedTypes: AdjustedProductType[],
  excludedSimulationItems: SimulationMimshakData[]
): ExcludedItemsHeuristicResult {
  const includedSet = new Set<number>(includedTypes);

  const countByType = (items: { adjusted_product_type?: number | null }[]) => {
    const map = new Map<number, number>();
    for (const item of items) {
      const t = item.adjusted_product_type;
      if (t != null && !includedSet.has(t)) {
        map.set(t, (map.get(t) ?? 0) + 1);
      }
    }
    return map;
  };

  const seenKeys = new Set<string>();
  const dedupedAllData = allData.filter(item => {
    const key = `${item.policy_number}|${item.handler_code}|${item.product_type}`;
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
  const allDataCounts = countByType(dedupedAllData);
  const excludedCounts = countByType(excludedSimulationItems);

  const missingTypes: AdjustedProductType[] = [];
  let expectedMinCount = 0;
  let actualCount = 0;

  for (const [type, count] of allDataCounts.entries()) {
    expectedMinCount += count;
    const saved = excludedCounts.get(type) ?? 0;
    actualCount += saved;
    if (saved < count) {
      missingTypes.push(type as AdjustedProductType);
    }
  }

  return {
    valid: missingTypes.length === 0,
    expectedMinCount,
    actualCount,
    missingTypes,
  };
}
